export class ApiError extends Error {
    code;
    status;
    details;
    constructor(code, message, status, details) {
        super(message);
        this.code = code;
        this.status = status;
        this.details = details;
        this.name = 'ApiError';
    }
}
const VALIDATION_REASON_LABELS = {
    TOO_SHORT: 'too short (min 3 characters)',
    TOO_LONG: 'too long (max 30 characters)',
    INVALID_CHARS: 'use only lowercase letters, numbers, and underscores',
    INVALID_EDGE_UNDERSCORE: 'cannot start or end with an underscore',
    RESERVED: 'that username is reserved',
};
/**
 * Prefer field-level Zod issues when present (e.g. username TOO_SHORT),
 * otherwise fall back to the top-level API message.
 */
export function formatApiErrorMessage(error, fallback) {
    if (!(error instanceof ApiError)) {
        return fallback;
    }
    const issues = error.details?.issues;
    if (!Array.isArray(issues) || issues.length === 0) {
        return error.message || fallback;
    }
    const parts = [];
    for (const raw of issues) {
        if (!raw || typeof raw !== 'object') {
            continue;
        }
        const issue = raw;
        const path = Array.isArray(issue.path)
            ? issue.path.filter((segment) => typeof segment === 'string' || typeof segment === 'number').join('.')
            : '';
        const message = typeof issue.message === 'string' ? (VALIDATION_REASON_LABELS[issue.message] ?? issue.message) : '';
        if (!message) {
            continue;
        }
        parts.push(path ? `${path}: ${message}` : message);
    }
    return parts.length > 0 ? parts.join('; ') : error.message || fallback;
}
const apiErrorListeners = new Set();
export function subscribeApiErrors(listener) {
    apiErrorListeners.add(listener);
    return () => {
        apiErrorListeners.delete(listener);
    };
}
export function isInsufficientBalanceError(error) {
    return error instanceof ApiError && error.code === 'INSUFFICIENT_BALANCE';
}
function emitApiError(error) {
    for (const listener of apiErrorListeners) {
        listener(error);
    }
}
export function createApiClient(config) {
    let refreshInFlight = null;
    const credentials = config.credentials ?? 'include';
    async function refreshAccessTokenOnce() {
        if (!config.refreshAccessToken) {
            return null;
        }
        if (!refreshInFlight) {
            refreshInFlight = config
                .refreshAccessToken()
                .catch(() => null)
                .finally(() => {
                refreshInFlight = null;
            });
        }
        return refreshInFlight;
    }
    async function request(path, init, didRetry = false) {
        const token = config.getAccessToken?.();
        const headers = new Headers(init?.headers);
        if (!headers.has('Content-Type') && init?.body) {
            headers.set('Content-Type', 'application/json');
        }
        if (token) {
            headers.set('Authorization', `Bearer ${token}`);
        }
        const extraHeaders = config.getHeaders?.();
        if (extraHeaders) {
            for (const [key, value] of Object.entries(extraHeaders)) {
                headers.set(key, value);
            }
        }
        const response = await fetch(`${config.baseUrl}${path}`, {
            ...init,
            headers,
            credentials,
        });
        if (response.status === 401 &&
            !didRetry &&
            !path.startsWith('/v1/auth/login') &&
            !path.startsWith('/v1/auth/register') &&
            !path.startsWith('/v1/auth/oauth') &&
            !path.startsWith('/v1/auth/refresh') &&
            !path.startsWith('/v1/auth/logout')) {
            const nextToken = await refreshAccessTokenOnce();
            if (nextToken) {
                return request(path, init, true);
            }
        }
        if (!response.ok) {
            const body = (await response.json().catch(() => null));
            const error = new ApiError(body?.code ?? 'UNKNOWN_ERROR', body?.message ?? response.statusText, response.status, body?.details);
            emitApiError(error);
            throw error;
        }
        if (response.status === 204) {
            return undefined;
        }
        return (await response.json());
    }
    return {
        health: () => request('/health'),
        ready: () => request('/health/ready'),
        register: (body) => request('/v1/auth/register', {
            method: 'POST',
            body: JSON.stringify(body),
        }),
        login: (body) => request('/v1/auth/login', {
            method: 'POST',
            body: JSON.stringify(body),
        }),
        oauthLogin: (body) => request('/v1/auth/oauth', {
            method: 'POST',
            body: JSON.stringify(body),
        }),
        refresh: (body = {}) => request('/v1/auth/refresh', {
            method: 'POST',
            body: JSON.stringify(body),
        }),
        logout: () => request('/v1/auth/logout', {
            method: 'POST',
        }),
        getMe: () => request('/v1/users/me'),
        checkUsername: (username) => request(`/v1/users/check-username?username=${encodeURIComponent(username)}`),
        getUserByUsername: (username) => request(`/v1/users/${encodeURIComponent(username)}`),
        getWallet: () => request('/v1/wallet'),
        getSpendWallet: () => request('/v1/wallet/spend'),
        getEarnWallet: () => request('/v1/wallet/earn'),
        withdrawEarn: (body) => request('/v1/wallet/earn/withdraw', {
            method: 'POST',
            body: JSON.stringify(body),
        }),
        releasePendingEarn: (body = {}) => request('/v1/wallet/earn/release', {
            method: 'POST',
            body: JSON.stringify(body),
        }),
        getSpendLimits: () => request('/v1/users/me/spend-limits'),
        updateSpendLimits: (body) => request('/v1/users/me/spend-limits', {
            method: 'PUT',
            body: JSON.stringify(body),
        }),
        updateModelProfile: (body) => request('/v1/models/me/profile', {
            method: 'PUT',
            body: JSON.stringify(body),
        }),
        getMyToys: () => request('/v1/models/me/toys'),
        getModelToys: (username) => request(`/v1/models/${encodeURIComponent(username)}/toys`),
        updateMyToys: (body) => request('/v1/models/me/toys', {
            method: 'PUT',
            body: JSON.stringify(body),
        }),
        getMyCloset: () => request('/v1/models/me/closet'),
        getModelCloset: (username) => request(`/v1/models/${encodeURIComponent(username)}/closet`),
        updateMyCloset: (body) => request('/v1/models/me/closet', {
            method: 'PUT',
            body: JSON.stringify(body),
        }),
        createToyControl: (body) => request('/v1/models/me/toys/controls', {
            method: 'POST',
            body: JSON.stringify(body),
        }),
        updateToyControl: (controlId, body) => request(`/v1/models/me/toys/controls/${encodeURIComponent(controlId)}`, { method: 'PUT', body: JSON.stringify(body) }),
        deleteToyControl: (controlId) => request(`/v1/models/me/toys/controls/${encodeURIComponent(controlId)}`, { method: 'DELETE' }),
        getMyJukebox: () => request('/v1/models/me/jukebox'),
        getModelJukebox: (username) => request(`/v1/models/${encodeURIComponent(username)}/jukebox`),
        updateMyJukebox: (body) => request('/v1/models/me/jukebox', {
            method: 'PUT',
            body: JSON.stringify(body),
        }),
        createJukeboxTrack: (body) => request('/v1/models/me/jukebox/tracks', {
            method: 'POST',
            body: JSON.stringify(body),
        }),
        updateJukeboxTrack: (trackId, body) => request(`/v1/models/me/jukebox/tracks/${encodeURIComponent(trackId)}`, { method: 'PUT', body: JSON.stringify(body) }),
        deleteJukeboxTrack: (trackId) => request(`/v1/models/me/jukebox/tracks/${encodeURIComponent(trackId)}`, { method: 'DELETE' }),
        listMyWishlist: () => request('/v1/models/me/wishlist'),
        listModelWishlist: (username) => request(`/v1/models/${encodeURIComponent(username)}/wishlist`),
        createWishlistItem: (body) => request('/v1/models/me/wishlist', {
            method: 'POST',
            body: JSON.stringify(body),
        }),
        updateWishlistItem: (itemId, body) => request(`/v1/models/me/wishlist/${encodeURIComponent(itemId)}`, { method: 'PUT', body: JSON.stringify(body) }),
        deleteWishlistItem: (itemId) => request(`/v1/models/me/wishlist/${encodeURIComponent(itemId)}`, { method: 'DELETE' }),
        getMyBoundaries: () => request('/v1/models/me/boundaries'),
        getModelBoundaries: (username) => request(`/v1/models/${encodeURIComponent(username)}/boundaries`),
        updateMyBoundaries: (body) => request('/v1/models/me/boundaries', {
            method: 'PUT',
            body: JSON.stringify(body),
        }),
        listMyDiscounts: () => request('/v1/models/me/discounts'),
        createDiscount: (body) => request('/v1/models/me/discounts', {
            method: 'POST',
            body: JSON.stringify(body),
        }),
        updateDiscount: (discountId, body) => request(`/v1/models/me/discounts/${encodeURIComponent(discountId)}`, { method: 'PUT', body: JSON.stringify(body) }),
        deleteDiscount: (discountId) => request(`/v1/models/me/discounts/${encodeURIComponent(discountId)}`, { method: 'DELETE' }),
        listCoinPackages: () => request('/v1/coins/packages'),
        quoteCoinPurchase: (body) => request('/v1/coins/purchase/quote', {
            method: 'POST',
            body: JSON.stringify(body),
        }),
        createCoinPurchase: (body) => request('/v1/coins/purchase', {
            method: 'POST',
            body: JSON.stringify(body),
        }),
        confirmCoinPurchase: (purchaseId) => request(`/v1/coins/purchase/${purchaseId}/confirm`, {
            method: 'POST',
        }),
        getComplianceStatus: () => request('/v1/compliance/status'),
        acknowledgeAgeEntry: () => request('/v1/compliance/entry-ack', {
            method: 'POST',
        }),
        submitAgeAttestation: (body) => request('/v1/compliance/attest', {
            method: 'POST',
            body: JSON.stringify(body),
        }),
        startAgeVerification: () => request('/v1/compliance/verify/start', {
            method: 'POST',
        }),
        confirmAgeVerification: () => request('/v1/compliance/verify/confirm', {
            method: 'POST',
        }),
        getGeoRegions: () => request('/v1/geo/regions'),
        getModelGeoBlocks: () => request('/v1/models/me/geo-blocks'),
        setModelGeoBlocks: (body) => request('/v1/models/me/geo-blocks', {
            method: 'PUT',
            body: JSON.stringify(body),
        }),
        createModelProfile: (body = {}) => request('/v1/models/me', {
            method: 'POST',
            body: JSON.stringify(body),
        }),
        getModelOnboarding: () => request('/v1/models/me/onboarding'),
        updateModelOnboarding: (body) => request('/v1/models/me/onboarding', {
            method: 'PUT',
            body: JSON.stringify(body),
        }),
        submitModelOnboarding: () => request('/v1/models/me/onboarding/submit', {
            method: 'POST',
        }),
        verifyModelIdentity: () => request('/v1/models/me/onboarding/identity', {
            method: 'POST',
        }),
        getModelProfile: () => request('/v1/models/me'),
        getModelStorefront: (username) => request(`/v1/models/${encodeURIComponent(username)}/storefront`),
        followModel: (username) => request(`/v1/models/${encodeURIComponent(username)}/follow`, {
            method: 'POST',
        }),
        unfollowModel: (username) => request(`/v1/models/${encodeURIComponent(username)}/follow`, {
            method: 'DELETE',
        }),
        favoriteModel: (username) => request(`/v1/models/${encodeURIComponent(username)}/favorite`, {
            method: 'POST',
        }),
        unfavoriteModel: (username) => request(`/v1/models/${encodeURIComponent(username)}/favorite`, {
            method: 'DELETE',
        }),
        listFollowing: () => request('/v1/users/me/following'),
        listFavorites: () => request('/v1/users/me/favorites'),
        reorderFavorites: (body) => request('/v1/users/me/favorites', {
            method: 'PUT',
            body: JSON.stringify(body),
        }),
        getUserProfile: (username) => request(`/v1/users/${encodeURIComponent(username)}/profile`),
        followUser: (username) => request(`/v1/users/${encodeURIComponent(username)}/follow`, {
            method: 'POST',
        }),
        unfollowUser: (username) => request(`/v1/users/${encodeURIComponent(username)}/follow`, {
            method: 'DELETE',
        }),
        favoriteUser: (username) => request(`/v1/users/${encodeURIComponent(username)}/favorite`, {
            method: 'POST',
        }),
        unfavoriteUser: (username) => request(`/v1/users/${encodeURIComponent(username)}/favorite`, {
            method: 'DELETE',
        }),
        listUserFavorites: () => request('/v1/users/me/favorite-users'),
        reorderUserFavorites: (body) => request('/v1/users/me/favorite-users', {
            method: 'PUT',
            body: JSON.stringify(body),
        }),
        updateMyProfile: (body) => request('/v1/users/me/profile', {
            method: 'PUT',
            body: JSON.stringify(body),
        }),
        listUserPosts: (username) => request(`/v1/users/${encodeURIComponent(username)}/posts`),
        createPost: (body) => request('/v1/users/me/posts', {
            method: 'POST',
            body: JSON.stringify(body),
        }),
        deletePost: (postId) => request(`/v1/users/me/posts/${encodeURIComponent(postId)}`, {
            method: 'DELETE',
        }),
        getNotificationPreferences: () => request('/v1/users/me/notification-preferences'),
        updateNotificationPreferences: (body) => request('/v1/users/me/notification-preferences', {
            method: 'PUT',
            body: JSON.stringify(body),
        }),
        updateModelNotifications: (username, body) => request(`/v1/users/me/following/${encodeURIComponent(username)}/notifications`, {
            method: 'PUT',
            body: JSON.stringify(body),
        }),
        getMyStats: () => request('/v1/users/me/stats'),
        getSiteLeaderboards: (window) => request(window ? `/v1/leaderboards?window=${encodeURIComponent(window)}` : '/v1/leaderboards'),
        likeMedia: (mediaItemId) => request(`/v1/media/${encodeURIComponent(mediaItemId)}/like`, {
            method: 'POST',
        }),
        unlikeMedia: (mediaItemId) => request(`/v1/media/${encodeURIComponent(mediaItemId)}/like`, {
            method: 'DELETE',
        }),
        updateModelAvailability: (body) => request('/v1/models/me/availability', {
            method: 'PUT',
            body: JSON.stringify(body),
        }),
        listShows: (status) => request(status ? `/v1/shows?status=${encodeURIComponent(status)}` : '/v1/shows'),
        listCams: (genders, paging) => {
            const params = new URLSearchParams();
            if (genders) {
                for (const gender of genders) {
                    params.append('gender', gender);
                }
            }
            if (paging?.onlinePage !== undefined) {
                params.set('onlinePage', String(paging.onlinePage));
            }
            if (paging?.offlinePage !== undefined) {
                params.set('offlinePage', String(paging.offlinePage));
            }
            if (paging?.limit !== undefined) {
                params.set('limit', String(paging.limit));
            }
            const query = params.toString();
            return request(query.length > 0 ? `/v1/shows/cams?${query}` : '/v1/shows/cams');
        },
        listUpcomingShows: () => request('/v1/shows/upcoming'),
        getShow: (showId) => request(`/v1/shows/${encodeURIComponent(showId)}`),
        getShowByUsername: (username) => request(`/v1/shows/live/${encodeURIComponent(username)}`),
        getFanClubStatus: (username) => request(`/v1/shows/live/${encodeURIComponent(username)}/fan-club`),
        joinFanClub: (username, body = {}) => request(`/v1/shows/live/${encodeURIComponent(username)}/fan-club/join`, { method: 'POST', body: JSON.stringify(body) }),
        listMyFanClubTiers: () => request('/v1/models/me/fan-club/tiers'),
        createFanClubTier: (body) => request('/v1/models/me/fan-club/tiers', {
            method: 'POST',
            body: JSON.stringify(body),
        }),
        updateFanClubTier: (tierId, body) => request(`/v1/models/me/fan-club/tiers/${encodeURIComponent(tierId)}`, { method: 'PUT', body: JSON.stringify(body) }),
        deleteFanClubTier: (tierId) => request(`/v1/models/me/fan-club/tiers/${encodeURIComponent(tierId)}`, { method: 'DELETE' }),
        listMyBlockSessions: () => request('/v1/models/me/block-sessions'),
        listModelBlockSessions: (username) => request(`/v1/models/${encodeURIComponent(username)}/block-sessions`),
        createBlockSession: (body) => request('/v1/models/me/block-sessions', {
            method: 'POST',
            body: JSON.stringify(body),
        }),
        updateBlockSession: (sessionId, body) => request(`/v1/models/me/block-sessions/${encodeURIComponent(sessionId)}`, { method: 'PUT', body: JSON.stringify(body) }),
        deleteBlockSession: (sessionId) => request(`/v1/models/me/block-sessions/${encodeURIComponent(sessionId)}`, { method: 'DELETE' }),
        bookBlockSession: (username, sessionId) => request(`/v1/models/${encodeURIComponent(username)}/block-sessions/${encodeURIComponent(sessionId)}/book`, { method: 'POST' }),
        listMySchedule: () => request('/v1/models/me/schedule'),
        listModelSchedule: (username) => request(`/v1/models/${encodeURIComponent(username)}/schedule`),
        createScheduleSlot: (body) => request('/v1/models/me/schedule', {
            method: 'POST',
            body: JSON.stringify(body),
        }),
        updateScheduleSlot: (slotId, body) => request(`/v1/models/me/schedule/${encodeURIComponent(slotId)}`, { method: 'PUT', body: JSON.stringify(body) }),
        deleteScheduleSlot: (slotId) => request(`/v1/models/me/schedule/${encodeURIComponent(slotId)}`, {
            method: 'DELETE',
        }),
        getModePass: () => request('/v1/mode-pass/me'),
        subscribeModePass: (body) => request('/v1/mode-pass/subscribe', {
            method: 'POST',
            body: JSON.stringify(body),
        }),
        getModePassAffiliateStats: () => request('/v1/models/me/mode-pass-affiliate'),
        listMediaBrowseCatalog: (limit) => request(`/v1/media/catalog${limit !== undefined ? `?limit=${encodeURIComponent(String(limit))}` : ''}`),
        updateMediaProgress: (mediaItemId, body) => request(`/v1/media/${encodeURIComponent(mediaItemId)}/progress`, { method: 'POST', body: JSON.stringify(body) }),
        listMyCustomVideoOrders: () => request('/v1/models/me/custom-videos'),
        listFanCustomVideoOrders: () => request('/v1/users/me/custom-videos'),
        createCustomVideoOrder: (username, body) => request(`/v1/models/${encodeURIComponent(username)}/custom-videos`, { method: 'POST', body: JSON.stringify(body) }),
        respondCustomVideoOrder: (orderId, body) => request(`/v1/models/me/custom-videos/${encodeURIComponent(orderId)}/respond`, { method: 'POST', body: JSON.stringify(body) }),
        createShow: (body = {}) => request('/v1/shows', {
            method: 'POST',
            body: JSON.stringify(body),
        }),
        endShow: (showId) => request(`/v1/shows/${encodeURIComponent(showId)}/end`, {
            method: 'POST',
            body: JSON.stringify({ clearQueue: true }),
        }),
        returnToPublic: (showId) => request(`/v1/shows/${encodeURIComponent(showId)}/return-to-public`, { method: 'POST' }),
        takeBreak: (showId) => request(`/v1/shows/${encodeURIComponent(showId)}/take-break`, {
            method: 'POST',
        }),
        resumeShow: (showId) => request(`/v1/shows/${encodeURIComponent(showId)}/resume`, {
            method: 'POST',
        }),
        markAway: (showId) => request(`/v1/shows/${encodeURIComponent(showId)}/away`, {
            method: 'POST',
        }),
        heartbeatShow: (showId) => request(`/v1/shows/${encodeURIComponent(showId)}/heartbeat`, {
            method: 'POST',
        }),
        broadcasterHeartbeat: (showId) => request(`/v1/shows/${encodeURIComponent(showId)}/broadcaster-heartbeat`, {
            method: 'POST',
        }),
        startScheduledShow: (showId) => request(`/v1/shows/${encodeURIComponent(showId)}/start`, {
            method: 'POST',
        }),
        transitionShow: (showId, body) => request(`/v1/shows/${encodeURIComponent(showId)}/transition`, {
            method: 'POST',
            body: JSON.stringify(body),
        }),
        listPrivateQueue: (showId) => request(`/v1/shows/${encodeURIComponent(showId)}/queue`),
        getPrivateQueueOptions: (showId) => request(`/v1/shows/${encodeURIComponent(showId)}/queue/options`),
        joinPrivateQueue: (showId, body) => request(`/v1/shows/${encodeURIComponent(showId)}/queue`, {
            method: 'POST',
            body: JSON.stringify(body),
        }),
        leavePrivateQueue: (showId) => request(`/v1/shows/${encodeURIComponent(showId)}/queue/leave`, {
            method: 'POST',
        }),
        startNextPrivateQueue: (showId) => request(`/v1/shows/${encodeURIComponent(showId)}/queue/start-next`, { method: 'POST' }),
        skipPrivateQueueEntry: (showId, entryId) => request(`/v1/shows/${encodeURIComponent(showId)}/queue/${encodeURIComponent(entryId)}/skip`, { method: 'POST' }),
        listShowInteractions: (showId) => request(`/v1/shows/${encodeURIComponent(showId)}/interactions`),
        getShowInteraction: (showId, interactionId) => request(`/v1/shows/${encodeURIComponent(showId)}/interactions/${encodeURIComponent(interactionId)}`),
        createShowInteraction: (showId, body) => request(`/v1/shows/${encodeURIComponent(showId)}/interactions`, {
            method: 'POST',
            body: JSON.stringify(body),
        }),
        acceptShowInteraction: (showId, interactionId) => request(`/v1/shows/${encodeURIComponent(showId)}/interactions/${encodeURIComponent(interactionId)}/accept`, { method: 'POST' }),
        declineShowInteraction: (showId, interactionId) => request(`/v1/shows/${encodeURIComponent(showId)}/interactions/${encodeURIComponent(interactionId)}/decline`, { method: 'POST' }),
        leaveShowInteraction: (showId, interactionId) => request(`/v1/shows/${encodeURIComponent(showId)}/interactions/${encodeURIComponent(interactionId)}/leave`, { method: 'POST' }),
        getJukeboxNowPlaying: (showId) => request(`/v1/shows/${encodeURIComponent(showId)}/jukebox/now-playing`),
        joinShow: (showId, body = {}) => request(`/v1/shows/${encodeURIComponent(showId)}/join`, {
            method: 'POST',
            body: JSON.stringify(body),
        }),
        listShowTypes: () => request('/v1/show-types'),
        getModelShowPricing: () => request('/v1/models/me/show-pricing'),
        getModelAnalytics: () => request('/v1/models/me/analytics'),
        applyShowDemandPricing: (showId) => request(`/v1/shows/${encodeURIComponent(showId)}/demand-pricing/apply`, { method: 'POST' }),
        updateModelShowPricing: (body) => request('/v1/models/me/show-pricing', {
            method: 'PUT',
            body: JSON.stringify(body),
        }),
        listMyTipMenu: () => request('/v1/models/me/tip-menu'),
        listModelTipMenu: (username) => request(`/v1/models/${encodeURIComponent(username)}/tip-menu`),
        createTipMenuItem: (body) => request('/v1/models/me/tip-menu', {
            method: 'POST',
            body: JSON.stringify(body),
        }),
        updateTipMenuItem: (itemId, body) => request(`/v1/models/me/tip-menu/${encodeURIComponent(itemId)}`, {
            method: 'PUT',
            body: JSON.stringify(body),
        }),
        deleteTipMenuItem: (itemId) => request(`/v1/models/me/tip-menu/${encodeURIComponent(itemId)}`, {
            method: 'DELETE',
        }),
        getModelRoomGames: (username) => request(`/v1/models/${encodeURIComponent(username)}/games`),
        getMyRoomGames: () => request('/v1/models/me/games'),
        updateMyRoomGames: (body) => request('/v1/models/me/games', {
            method: 'PUT',
            body: JSON.stringify(body),
        }),
        createRoomGamePrize: (body) => request('/v1/models/me/games/prizes', {
            method: 'POST',
            body: JSON.stringify(body),
        }),
        updateRoomGamePrize: (prizeId, body) => request(`/v1/models/me/games/prizes/${encodeURIComponent(prizeId)}`, {
            method: 'PUT',
            body: JSON.stringify(body),
        }),
        deleteRoomGamePrize: (prizeId) => request(`/v1/models/me/games/prizes/${encodeURIComponent(prizeId)}`, {
            method: 'DELETE',
        }),
        playRoomGame: (showId, body) => request(`/v1/shows/${encodeURIComponent(showId)}/games/play`, {
            method: 'POST',
            body: JSON.stringify(body),
        }),
        createPrivateShowRequest: (body) => request('/v1/private-shows/requests', {
            method: 'POST',
            body: JSON.stringify(body),
        }),
        listPrivateShowRequests: () => request('/v1/private-shows/requests'),
        acceptPrivateShowRequest: (requestId) => request(`/v1/private-shows/requests/${encodeURIComponent(requestId)}/accept`, { method: 'POST' }),
        declinePrivateShowRequest: (requestId) => request(`/v1/private-shows/requests/${encodeURIComponent(requestId)}/decline`, { method: 'POST' }),
        leaveShow: (showId) => request(`/v1/shows/${encodeURIComponent(showId)}/leave`, {
            method: 'POST',
        }),
        upgradeExclusive: (showId) => request(`/v1/shows/${encodeURIComponent(showId)}/upgrade-exclusive`, { method: 'POST' }),
        submitShowFeedback: (showId, body) => request(`/v1/shows/${encodeURIComponent(showId)}/feedback`, {
            method: 'POST',
            body: JSON.stringify(body),
        }),
        listShowAudience: (showId) => request(`/v1/shows/${encodeURIComponent(showId)}/viewers`),
        muteShowUser: (showId, body) => request(`/v1/shows/${encodeURIComponent(showId)}/moderation/mute`, { method: 'POST', body: JSON.stringify(body) }),
        kickShowUser: (showId, body) => request(`/v1/shows/${encodeURIComponent(showId)}/moderation/kick`, { method: 'POST', body: JSON.stringify(body) }),
        banShowUser: (showId, body) => request(`/v1/shows/${encodeURIComponent(showId)}/moderation/ban`, { method: 'POST', body: JSON.stringify(body) }),
        unbanShowUser: (showId, body) => request(`/v1/shows/${encodeURIComponent(showId)}/moderation/unban`, { method: 'POST', body: JSON.stringify(body) }),
        payToUnban: (showId) => request(`/v1/shows/${encodeURIComponent(showId)}/moderation/pay-unban`, { method: 'POST' }),
        voteCrowdBan: (showId, body) => request(`/v1/shows/${encodeURIComponent(showId)}/moderation/crowd-ban/vote`, { method: 'POST', body: JSON.stringify(body) }),
        resolveCrowdBan: (showId, roundId, body) => request(`/v1/shows/${encodeURIComponent(showId)}/moderation/crowd-ban/${encodeURIComponent(roundId)}/resolve`, { method: 'POST', body: JSON.stringify(body) }),
        listModerators: () => request('/v1/models/me/moderators'),
        assignModerator: (body) => request('/v1/models/me/moderators', { method: 'POST', body: JSON.stringify(body) }),
        removeModerator: (userId) => request(`/v1/models/me/moderators/${encodeURIComponent(userId)}`, { method: 'DELETE' }),
        getUnbanPrice: () => request('/v1/models/me/unban-price'),
        updateUnbanPrice: (body) => request('/v1/models/me/unban-price', {
            method: 'PUT',
            body: JSON.stringify(body),
        }),
        touchShowPresence: (showId, body) => request(`/v1/shows/${encodeURIComponent(showId)}/presence`, {
            method: 'POST',
            body: JSON.stringify(body),
        }),
        leaveShowPresence: (showId, body) => request(`/v1/shows/${encodeURIComponent(showId)}/presence`, {
            method: 'DELETE',
            body: JSON.stringify(body),
        }),
        listShowTips: (showId, limit) => request(`/v1/shows/${encodeURIComponent(showId)}/tips${limit !== undefined ? `?limit=${encodeURIComponent(String(limit))}` : ''}`),
        listShowChat: (showId, options) => {
            const params = new URLSearchParams();
            if (options?.since) {
                params.set('since', options.since);
            }
            if (options?.limit !== undefined) {
                params.set('limit', String(options.limit));
            }
            const query = params.toString();
            return request(`/v1/shows/${encodeURIComponent(showId)}/chat/messages${query ? `?${query}` : ''}`);
        },
        announceShowEntry: (showId) => request(`/v1/shows/${encodeURIComponent(showId)}/chat/entrance`, { method: 'POST' }),
        sendChatMessage: (showId, body) => request(`/v1/shows/${encodeURIComponent(showId)}/chat/messages`, {
            method: 'POST',
            body: JSON.stringify(body),
        }),
        deleteChatMessage: (showId, messageId) => request(`/v1/shows/${encodeURIComponent(showId)}/chat/messages/${encodeURIComponent(messageId)}`, { method: 'DELETE' }),
        getModelChatConfig: () => request('/v1/models/me/chat-config'),
        updateModelChatConfig: (body) => request('/v1/models/me/chat-config', {
            method: 'PUT',
            body: JSON.stringify(body),
        }),
        getPrivateChat: (username, options) => {
            const params = new URLSearchParams();
            if (options?.fanUserId) {
                params.set('fanUserId', options.fanUserId);
            }
            const query = params.toString();
            return request(`/v1/models/${encodeURIComponent(username)}/private-chat${query ? `?${query}` : ''}`);
        },
        listPrivateChatThreads: () => request('/v1/models/me/private-chat/threads'),
        sendPrivateChatMessage: (username, body) => request(`/v1/models/${encodeURIComponent(username)}/private-chat/messages`, {
            method: 'POST',
            body: JSON.stringify(body),
        }),
        sendTip: (body) => request('/v1/tips', {
            method: 'POST',
            body: JSON.stringify(body),
        }),
        listBlocks: () => request('/v1/safety/blocks'),
        blockUser: (body) => request('/v1/safety/blocks', {
            method: 'POST',
            body: JSON.stringify(body),
        }),
        unblockUser: (userId) => request(`/v1/safety/blocks/${encodeURIComponent(userId)}`, {
            method: 'DELETE',
        }),
        createReport: (body) => request('/v1/safety/reports', {
            method: 'POST',
            body: JSON.stringify(body),
        }),
        getAdminOverview: () => request('/v1/admin/overview'),
        getAdminPlatformWarmupCron: () => request('/v1/admin/platform/warmup-cron'),
        setAdminPlatformWarmupCron: (body) => request('/v1/admin/platform/warmup-cron', {
            method: 'PUT',
            body: JSON.stringify(body),
        }),
        listAdminCoinPackages: () => request('/v1/admin/coin-packages'),
        createAdminCoinPackage: (body) => request('/v1/admin/coin-packages', {
            method: 'POST',
            body: JSON.stringify(body),
        }),
        updateAdminCoinPackage: (packageId, body) => request(`/v1/admin/coin-packages/${encodeURIComponent(packageId)}`, {
            method: 'PUT',
            body: JSON.stringify(body),
        }),
        deleteAdminCoinPackage: (packageId) => request(`/v1/admin/coin-packages/${encodeURIComponent(packageId)}`, { method: 'DELETE' }),
        getAdminPurchasesOverview: () => request('/v1/admin/purchases/overview'),
        listAdminCoinPurchases: (options) => {
            const params = new URLSearchParams();
            if (options?.limit !== undefined) {
                params.set('limit', String(options.limit));
            }
            if (options?.packageId !== undefined) {
                params.set('packageId', options.packageId);
            }
            const query = params.toString();
            return request(`/v1/admin/coin-purchases${query ? `?${query}` : ''}`);
        },
        listAdminContentPurchases: (options) => {
            const params = new URLSearchParams();
            if (options?.limit !== undefined) {
                params.set('limit', String(options.limit));
            }
            if (options?.mediaItemId) {
                params.set('mediaItemId', options.mediaItemId);
            }
            const query = params.toString();
            return request(`/v1/admin/content-purchases${query ? `?${query}` : ''}`);
        },
        listReports: () => request('/v1/admin/reports'),
        updateReportStatus: (reportId, body) => request(`/v1/admin/reports/${encodeURIComponent(reportId)}`, {
            method: 'PATCH',
            body: JSON.stringify(body),
        }),
        listStaffInvites: () => request('/v1/admin/invites'),
        createStaffInvite: (body) => request('/v1/admin/invites', {
            method: 'POST',
            body: JSON.stringify(body),
        }),
        acceptStaffInvite: (body) => request('/v1/admin/invites/accept', {
            method: 'POST',
            body: JSON.stringify(body),
        }),
        listAlphaInvites: () => request('/v1/admin/alpha-invites'),
        createAlphaInvite: (body) => request('/v1/admin/alpha-invites', {
            method: 'POST',
            body: JSON.stringify(body),
        }),
        revokeAlphaInvite: (inviteId) => request(`/v1/admin/alpha-invites/${encodeURIComponent(inviteId)}/revoke`, { method: 'POST' }),
        listTesterSessions: () => request('/v1/admin/tester-sessions'),
        acceptAlphaInvite: (body) => request('/v1/alpha/invites/accept', {
            method: 'POST',
            body: JSON.stringify(body),
        }),
        getTesterSession: () => request('/v1/alpha/session'),
        completeTesterStep: (stepId) => request(`/v1/alpha/steps/${encodeURIComponent(stepId)}/complete`, { method: 'POST' }),
        submitTesterFeedback: (body) => request('/v1/alpha/feedback', {
            method: 'POST',
            body: JSON.stringify(body),
        }),
        getStaffMfaStatus: () => request('/v1/admin/mfa'),
        enrollStaffMfa: () => request('/v1/admin/mfa/enroll', {
            method: 'POST',
        }),
        confirmStaffMfa: (body) => request('/v1/admin/mfa/confirm', {
            method: 'POST',
            body: JSON.stringify(body),
        }),
        getExperience: () => request('/v1/users/me/experience'),
        updateExperience: (body) => request('/v1/users/me/experience', {
            method: 'PUT',
            body: JSON.stringify(body),
        }),
        getTurnOns: () => request('/v1/users/me/turn-ons'),
        updateTurnOns: (body) => request('/v1/users/me/turn-ons', {
            method: 'PUT',
            body: JSON.stringify(body),
        }),
        listMediaCatalog: (limit) => request(`/v1/media${limit !== undefined ? `?limit=${encodeURIComponent(String(limit))}` : ''}`),
        getMediaItem: (mediaItemId) => request(`/v1/media/${encodeURIComponent(mediaItemId)}`),
        getVideo: (username, slug) => request(`/v1/media/${encodeURIComponent(username)}/videos/${encodeURIComponent(slug)}`),
        purchaseMediaItem: (mediaItemId) => request(`/v1/media/${encodeURIComponent(mediaItemId)}/purchase`, {
            method: 'POST',
        }),
        listLibrary: () => request('/v1/library'),
        listCollections: () => request('/v1/library/collections'),
        saveCollectionItem: (body) => request('/v1/library/collections/items', {
            method: 'POST',
            body: JSON.stringify(body),
        }),
        removeCollectionItem: (itemId) => request(`/v1/library/collections/items/${encodeURIComponent(itemId)}`, {
            method: 'DELETE',
        }),
        captureShowSnapshot: (showId, body) => request(`/v1/shows/${encodeURIComponent(showId)}/snapshot`, {
            method: 'POST',
            body: JSON.stringify(body),
        }),
        captureShowRecording: (showId, body) => request(`/v1/shows/${encodeURIComponent(showId)}/recordings`, {
            method: 'POST',
            body: JSON.stringify(body),
        }),
        listModelMedia: () => request('/v1/models/me/media'),
        createMediaItem: (body) => request('/v1/models/me/media', {
            method: 'POST',
            body: JSON.stringify(body),
        }),
        publishMediaItem: (mediaItemId) => request(`/v1/models/me/media/${encodeURIComponent(mediaItemId)}/publish`, { method: 'POST' }),
    };
}
export { createShowRealtimeClient, } from './realtime.js';
//# sourceMappingURL=index.js.map